import { useEffect, useState } from 'react';

import { useSplit } from 'src/shared/hooks';
import { useQueryParam } from 'src/shared/hooks/useQueryParam';

type TreatmentConfig = object;
type Split = { treatment: string; config: TreatmentConfig };
type SplitOverride = {
  [key: string]: Split;
};

const parseSplit = (splits: string) => {
  const overrideSet: SplitOverride = {};
  const keyValuePair = splits.split(',');

  keyValuePair.forEach(pair => {
    const [key, value] = pair.split('=');

    if (key && value) {
      overrideSet[key] = { treatment: value, config: {} };
    }
  });

  return overrideSet;
};

/**
 * useSplitOverride is a custom hook that overrides the treatment of a split test.
 *
 * @param {string} splitName - The name of the split test to override.
 *
 * @returns {Split | undefined} - Returns an object of type Split if the splitName exists in the query string, undefined otherwise.
 * The Split object has two properties: 'treatment' which is a string, and 'config' which is an object.
 *
 * The hook expects a query string parameter named 'splits' with a value in the format of 'splitName1=treatment1,splitName2=treatment2,...'.
 * Each splitName and treatment pair is separated by a comma, and within each pair, the splitName and treatment are separated by an equals sign.
 * For example, a valid 'splits' query string could look like this: 'meow-projects=off,web-onboarding-phase-one-log-in=on'.
 *
 * The hook uses the useSplit and useQueryParam hooks to get the split manager and the value of the 'splits' query string parameter.
 * It then parses the 'splits' value into an object where each key is a splitName and each value is a Split object.
 * If the splitName passed to the hook exists in this object, it returns the corresponding Split object.
 * Otherwise, it returns undefined.
 */
export const useSplitOverride = (splitName: string): Split | undefined => {
  const { manager } = useSplit();

  const splits = useQueryParam('splits', true);
  const [splitOverride, setSplitOverride] = useState<SplitOverride>({});

  useEffect(() => {
    if (!splits) return;

    const parsedSplits = parseSplit(splits);

    const getTreatments = async () => {
      const splitObj: SplitOverride = {};
      if (manager) {
        const man = await manager;
        if (man) {
          Object.keys(parsedSplits).forEach(key => {
            const treatmentWithConfig = man.split(key);
            const { treatment } = parsedSplits[key];
            const config = treatmentWithConfig?.configs[treatment];

            splitObj[key] = {
              treatment,
              config: config
                ? (JSON.parse(config) as unknown as TreatmentConfig)
                : {},
            };
          });

          setSplitOverride(splitObj);
        }
      }
    };
    void getTreatments();
  }, [splits, manager, setSplitOverride]);

  return splitOverride[splitName];
};
